import { isDemo } from "../App";
import { axiosRequest } from "../axiosConfig";
import { tableData } from "./tableData";

class FaxInboxCacheManager {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 50;
    this.storageKey = "faxInboxData";
  }

  async readFromCache(params) {
    try {
      const cacheKey = this.generateCacheKey(params);

      // Check pre-cached data first
      if (tableData) {
        console.log("Found fax data in pre-cached tableData");
        return tableData;
      }

      // Check in-memory cache
      if (this.cache.has(cacheKey)) {
        console.log("Found fax data in memory cache");
        return this.cache.get(cacheKey).data;
      }

      return null;
    } catch (error) {
      console.error("Error reading from fax inbox cache:", error);
      return null;
    }
  }

  async writeToCache(params, data) {
    try {
      const cacheKey = this.generateCacheKey(params);
      const cacheEntry = {
        data,
        timestamp: new Date().toISOString(),
      };

      // Update in-memory cache
      this.cache.set(cacheKey, cacheEntry);

      // Maintain cache size limit
      if (this.cache.size > this.maxCacheSize) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }

      // Update persistent storage
      if (typeof sessionStorage !== "undefined") {
        try {
          const persistentCache = {};
          this.cache.forEach((value, key) => {
            persistentCache[key] = value;
          });
          sessionStorage.setItem(
            this.storageKey,
            JSON.stringify(persistentCache),
          );
        } catch (err) {
          console.log("Error writing to persistent cache:", err);
        }
      }
    } catch (error) {
      console.error("Error writing to cache:", error);
    }
  }

  generateCacheKey(params) {
    const { alias, callerid, faxid, pagenum, status, sortBy, orderBy } = params;
    return `inbox_${alias}_${callerid}_${faxid}_${pagenum}_${status}_${sortBy}_${orderBy}`;
  }

  async getFaxInboxData(
    alias = "null",
    callerid = "null",
    faxid = "null",
    pagenum = 1,
    status = "null",
    sortBy = "receivedDate",
    orderBy = "desc",
  ) {
    try {
      const params = {
        alias,
        callerid,
        faxid,
        pagenum,
        status,
        sortBy,
        orderBy,
      };

      // Try to get from cache first
      const cachedData = await this.readFromCache(params);
      if (isDemo && cachedData) {
        return cachedData;
      }

      // If not in cache, fetch from API
      console.log("Fetching fax inbox data from API");
      callerid = callerid?.replace(/[- )(]/g, "");

      const response = await axiosRequest.get(
        `getfaxinbox/alias/${alias}/callerid/${callerid}/faxid/${faxid}/pageindex/${pagenum}/status/${status}`,
        {
          params: {
            sortExpression: sortBy,
            sortOrder: orderBy,
          },
        },
      );

      // Cache the response
      await this.writeToCache(params, response.data);

      return response.data;
    } catch (error) {
      console.error("Error fetching fax inbox:", error);
      if (error?.response?.data?.length < 2) {
        throw new Error(error?.response?.data[0]?.Message);
      } else {
        throw new Error(error?.response?.data || error.message);
      }
    }
  }
}

// Export singleton instance
export const faxInboxCacheManager = new FaxInboxCacheManager();
